import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { Badge } from '@/shared/ui/Badge';
import { formatDate } from '@/shared/utils/format';
import { agencyMetaAdsApi } from '../../api/agencyMetaAdsApi';
import { agencyKeys } from '../../queryKeys';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './MetaIntegrationCard.module.css';

/**
 * The agency-wide Meta System User token and the ad accounts it can see.
 *
 * Rendered on Agency Settings and again under Practice › Modules › Ads, where
 * the per-location account picker sits directly beneath it.
 */
export function MetaIntegrationCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [tokenInput, setTokenInput] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: agencyKeys.metaTokenStatus(),
    queryFn: () => agencyMetaAdsApi.getTokenStatus(),
  });

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: agencyKeys.metaAvailableAccounts(),
    queryFn: () => agencyMetaAdsApi.getAvailableAccounts(),
    enabled: status?.hasToken === true && !status.lastError,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: agencyKeys.metaTokenStatus() });
    queryClient.invalidateQueries({ queryKey: agencyKeys.metaAvailableAccounts() });
  };

  const saveTokenMutation = useMutation({
    mutationFn: () => agencyMetaAdsApi.setToken(tokenInput),
    onSuccess: (result) => {
      toast.success(`Token validated — ${result.accountCount} ad account(s) accessible.`);
      setEditing(false);
      setTokenInput('');
      invalidateAll();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to save token.'),
  });

  const clearTokenMutation = useMutation({
    mutationFn: () => agencyMetaAdsApi.clearToken(),
    onSuccess: () => {
      toast.success('Token removed.');
      invalidateAll();
    },
    onError: () => toast.error('Failed to remove token.'),
  });

  if (isLoading) {
    return (
      <Card title="Meta Ads">
        <Spinner size="sm" />
      </Card>
    );
  }

  const showForm = editing || !status?.hasToken;

  return (
    <Card title="Meta Ads">
      <p className={patterns.panelSubtitle}>
        Configure a Meta Business Manager <strong>System User</strong> token with Full Control over
        your partner-shared client ad accounts. This single token covers every practice in your
        agency — clients don&apos;t need to OAuth-connect individually. Ad accounts are then
        assigned per location below.
      </p>

      {status?.hasToken && (
        <div className={`${styles.statusGrid} ${styles.mt4}`}>
          <span className={styles.statusLabel}>Status</span>
          <span>
            {status.lastError ? (
              <Badge variant="error" size="sm">
                Token error
              </Badge>
            ) : (
              <Badge variant="success" size="sm">
                Connected
              </Badge>
            )}
          </span>

          <span className={styles.statusLabel}>Set</span>
          <span className={styles.statusValue}>
            {status.setAt ? formatDate(status.setAt) : '—'}
          </span>

          {typeof status.accountCount === 'number' && (
            <>
              <span className={styles.statusLabel}>Ad accounts</span>
              <span className={styles.statusValue}>{status.accountCount}</span>
            </>
          )}

          {status.lastError && (
            <>
              <span className={styles.statusLabel}>Last error</span>
              <span className={styles.errorRow}>{status.lastError}</span>
            </>
          )}
        </div>
      )}

      {showForm ? (
        <div className={`${styles.formRow} ${styles.mt4}`}>
          <Input
            label="System User Access Token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="EAAOSdxc66s8B..."
            fullWidth
            type="password"
            autoComplete="off"
            spellCheck={false}
          />
          <p className={`${patterns.panelSubtitle} ${styles.m0}`}>
            Generate this in Meta Business Settings → System Users → your API user → Generate token.
            Required scopes: <code>ads_read</code>, <code>read_insights</code>,{' '}
            <code>ads_management</code>.
          </p>
          <div className={styles.actions}>
            {status?.hasToken && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setTokenInput('');
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              loading={saveTokenMutation.isPending}
              disabled={!tokenInput.trim()}
              onClick={() => saveTokenMutation.mutate()}
            >
              {status?.hasToken ? 'Replace Token' : 'Save Token'}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            loading={clearTokenMutation.isPending}
            onClick={() => clearTokenMutation.mutate()}
          >
            Remove Token
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Replace Token
          </Button>
        </div>
      )}

      {status?.hasToken && !status.lastError && (
        <div className={styles.mt6}>
          <h4 className={patterns.sectionTitle}>Accessible ad accounts</h4>
          {accountsLoading ? (
            <Spinner size="sm" />
          ) : (accounts ?? []).length === 0 ? (
            <p className={styles.accountsEmpty}>
              The System User can&apos;t see any ad accounts yet. In Meta Business Manager, add
              client ad accounts as Partner assets and grant Full Control to this System User.
            </p>
          ) : (
            <table className={styles.accountsTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Account ID</th>
                  <th>Currency</th>
                  <th>Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {accounts!.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td className="mono">{a.id}</td>
                    <td>{a.currency}</td>
                    <td>
                      {a.assignedLocations.length > 0 ? (
                        <span className={styles.assignedLink}>
                          {a.assignedLocations.map((l) => l.name).join(', ')}
                        </span>
                      ) : (
                        <span className={styles.unassigned}>Unassigned</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
